// components/ReportExport.js
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";

export default function ReportExport({ currentEvent }) {
  const { t, i18n } = useTranslation();

  if (!currentEvent) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center py-5">
          <div className="fs-1">📄</div>
          <h4>{t("report.no_event")}</h4>
          <p>{t("report.select_event")}</p>
        </div>
      </div>
    );
  }

  const guests = currentEvent.guests || [];
  const services = currentEvent.services || [];
  const tasks = currentEvent.tasks || [];
  const seating = currentEvent.seating || [];

  // Guest totals
  const totalGuests = guests.length;
  const totalAdults = guests.reduce((sum, g) => sum + (g.adults || 1), 0);
  const totalChildren = guests.reduce((sum, g) => sum + (g.children || 0), 0);
  const totalPersons = totalAdults + totalChildren;
  const invitedCount = guests.filter((g) => g.invited).length;

  // Confirmation breakdown (supports legacy boolean true → 'accepted')
  const normalizeConfirmed = (v) =>
    v === true ? "accepted" : v === false ? null : v;

  const acceptedGuests   = guests.filter((g) => normalizeConfirmed(g.confirmed) === "accepted");
  const maybeGuests      = guests.filter((g) => normalizeConfirmed(g.confirmed) === "maybe");
  const declinedGuests   = guests.filter((g) => normalizeConfirmed(g.confirmed) === "declined");
  const noResponseGuests = guests.filter((g) => g.invited && !normalizeConfirmed(g.confirmed));

  const acceptedCount   = acceptedGuests.length;
  const maybeCount      = maybeGuests.length;
  const declinedCount   = declinedGuests.length;
  const noResponseCount = noResponseGuests.length;

  const personCount = (arr) => ({
    adults:   arr.reduce((s, g) => s + (g.adults || 1), 0),
    children: arr.reduce((s, g) => s + (g.children || 0), 0),
    total:    arr.reduce((s, g) => s + (g.adults || 1) + (g.children || 0), 0),
  });
  const acceptedPersons   = personCount(acceptedGuests);
  const maybePersons      = personCount(maybeGuests);
  const declinedPersons   = personCount(declinedGuests);
  const noResponsePersons = personCount(noResponseGuests);

  const confirmationLabel = (v) => {
    const n = normalizeConfirmed(v);
    if (n === "accepted") return `✅ ${t("guests.accepted")}`;
    if (n === "maybe") return `❓ ${t("guests.maybe")}`;
    if (n === "declined") return `❌ ${t("guests.declined")}`;
    return `⏳ ${t("guests.no_response")}`;
  };

  // Seating stats
  const tablePersons = (tb) =>
    (tb.guestIds || []).reduce((sum, gid) => {
      const g = guests.find(x => x.id === gid);
      return sum + (g ? (g.adults || 1) + (g.children || 0) : 0);
    }, 0);
  const totalSeats = seating.reduce((s, tb) => s + (tb.seats || 0), 0);
  const totalSeated = seating.reduce((s, tb) => s + tablePersons(tb), 0);
  const assignedGuestIds = new Set(seating.flatMap(tb => tb.guestIds || []));
  const unassignedCount = guests.filter(g => !assignedGuestIds.has(g.id)).length;

  const giftCount = guests.filter((g) => g.giftValue && g.giftValue > 0).length;
  const totalGifts = guests.reduce((sum, g) => sum + (g.giftValue || 0), 0);
  const avgGift = totalGuests > 0 ? totalGifts / totalGuests : 0;

  const totalExpenses = services.reduce((sum, s) => sum + (s.cost || 0), 0);
  const paidExpenses = services
    .filter((s) => s.paid)
    .reduce((sum, s) => sum + (s.cost || 0), 0);
  const pendingExpenses = totalExpenses - paidExpenses;
  const balance = totalGifts - totalExpenses;

  const completedTasks = tasks.filter((task) => task.completed).length;
  const totalTasks = tasks.length;

  // Export to Excel
  const exportToExcel = () => {
    const locale = i18n.language === "el" ? "el-GR" : "en-US";
    const workbook = XLSX.utils.book_new();

    // --- Summary sheet ---
    const summaryData = [
      [t("report.workbook_title")],
      [t("report.event_label"), currentEvent.name],
      [t("report.date_label"), new Date().toLocaleString(locale)],
      [""],
      [t("report.general_statistics")],
      [t("report.guest"), totalGuests],
      [t("report.total_adults"), totalAdults],
      [t("report.total_children"), totalChildren],
      [t("report.total_persons"), totalPersons],
      [t("report.invited"), invitedCount],
      [""],
      [t("report.confirmation_breakdown")],
      ["", t("report.guest_groups"), t("guests.total_persons"), t("guests.adults_short"), t("guests.children_short")],
      [
        `✅ ${t("guests.accepted")}`,
        acceptedCount,
        acceptedPersons.total,
        acceptedPersons.adults,
        acceptedPersons.children,
      ],
      [
        `❓ ${t("guests.maybe")}`,
        maybeCount,
        maybePersons.total,
        maybePersons.adults,
        maybePersons.children,
      ],
      [
        `❌ ${t("guests.declined")}`,
        declinedCount,
        declinedPersons.total,
        declinedPersons.adults,
        declinedPersons.children,
      ],
      [
        `⏳ ${t("guests.no_response")}`,
        noResponseCount,
        noResponsePersons.total,
        noResponsePersons.adults,
        noResponsePersons.children,
      ],
      [""],
      [t("report.financial_data")],
      [t("report.total_income"), `€${totalGifts.toFixed(2)}`],
      [t("report.total_expenses"), `€${totalExpenses.toFixed(2)}`],
      [t("report.paid_expenses"), `€${paidExpenses.toFixed(2)}`],
      [t("report.pending_expenses"), `€${pendingExpenses.toFixed(2)}`],
      [t("report.net_balance"), `€${balance.toFixed(2)}`],
      [t("report.avg_gift_value"), `€${avgGift.toFixed(2)}`],
      [""],
      [t("report.tasks_section")],
      [t("report.tasks_completed"), `${completedTasks}/${totalTasks}`],
      [t("report.tasks_pending"), `${totalTasks - completedTasks}`],
      [
        t("report.completion_rate"),
        `${totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}%`,
      ],
      [""],
      [t("nav.seating")],
      [t("seating.total_tables"), seating.length],
      [t("seating.total_seats"), totalSeats],
      [t("seating.persons_seated"), totalSeated],
      [t("seating.empty_seats"), Math.max(0, totalSeats - totalSeated)],
      [t("seating.unassigned_guests"), unassignedCount],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, t("report.summary"));

    // --- Guests sheet ---
    const guestsData = [
      [
        t("report.guest_name"),
        t("report.guest_invited"),
        t("report.guest_confirmed"),
        t("report.guest_adults"),
        t("report.guest_children"),
        t("report.guest_total_persons"),
        t("report.guest_amount"),
      ],
    ];

    guests.forEach((g) => {
      guestsData.push([
        g.name,
        g.invited ? t("common.yes") : t("common.no"),
        confirmationLabel(g.confirmed),
        g.adults || 1,
        g.children || 0,
        (g.adults || 1) + (g.children || 0),
        g.giftValue ? g.giftValue.toFixed(2) : "0.00",
      ]);
    });

    guestsData.push([
      "",
      "",
      "",
      "",
      "",
      t("report.total"),
      totalGifts.toFixed(2),
    ]);
    guestsData.push([
      "",
      "",
      "",
      "",
      "",
      t("report.average"),
      avgGift.toFixed(2),
    ]);

    const guestsSheet = XLSX.utils.aoa_to_sheet(guestsData);
    XLSX.utils.book_append_sheet(workbook, guestsSheet, t("report.guest_list"));

    // --- Services sheet ---
    const servicesData = [
      [
        t("report.service_name"),
        t("report.service_cost"),
        t("report.service_status"),
        t("report.service_provider"),
      ],
    ];
    services.forEach((s) => {
      servicesData.push([
        s.name,
        s.cost.toFixed(2),
        s.paid ? t("report.paid") : t("report.pending"),
        s.provider || "-",
      ]);
    });
    servicesData.push(["", "", "", ""]);
    servicesData.push([
      t("report.total_expenses"),
      totalExpenses.toFixed(2),
      "",
      "",
    ]);
    servicesData.push([
      t("report.paid_expenses"),
      paidExpenses.toFixed(2),
      "",
      "",
    ]);
    servicesData.push([
      t("report.pending_expenses"),
      pendingExpenses.toFixed(2),
      "",
      "",
    ]);

    const servicesSheet = XLSX.utils.aoa_to_sheet(servicesData);
    XLSX.utils.book_append_sheet(workbook, servicesSheet, t("report.services"));

    // --- Tasks sheet ---
    const tasksData = [
      [t("report.task_name"), t("report.task_notes"), t("report.task_status")],
    ];
    tasks.forEach((task) => {
      tasksData.push([
        task.title,
        task.notes || "-",
        task.completed
          ? `${t("report.completed")} ${t("report.task_done")}`
          : `${t("report.pending")} ${t("report.task_pending")}`,
      ]);
    });

    const tasksSheet = XLSX.utils.aoa_to_sheet(tasksData);
    XLSX.utils.book_append_sheet(workbook, tasksSheet, t("report.tasks"));

    // --- Finance sheet ---
    const financeData = [
      [t("report.financial_analysis")],
      [""],
      [t("report.revenue_gifts")],
      [t("report.guest_label"), t("report.gift_amount")],
    ];
    guests
      .filter((g) => g.giftValue && g.giftValue > 0)
      .forEach((g) => {
        financeData.push([g.name, g.giftValue.toFixed(2)]);
      });
    financeData.push(["", t("report.total"), totalGifts.toFixed(2)]);
    financeData.push(["", "", ""]);
    financeData.push([t("report.expenses_label")]);
    financeData.push([
      t("report.service_label"),
      t("report.cost_label"),
      t("report.status_label"),
    ]);
    services.forEach((s) => {
      financeData.push([
        s.name,
        s.cost.toFixed(2),
        s.paid ? t("report.paid") : t("report.pending"),
      ]);
    });
    financeData.push([
      "",
      t("report.total_expenses"),
      totalExpenses.toFixed(2),
    ]);
    financeData.push(["", "", ""]);
    financeData.push([
      t("report.net_balance_label"),
      "",
      `${balance >= 0 ? "+" : ""}${balance.toFixed(2)} €`,
    ]);
    financeData.push([
      t("report.balance_status_label"),
      "",
      balance >= 0 ? t("report.profitable") : t("report.loss"),
    ]);

    const financeSheet = XLSX.utils.aoa_to_sheet(financeData);
    XLSX.utils.book_append_sheet(workbook, financeSheet, t("report.financial_analysis"));

    // --- Seating sheet ---
    if (seating.length > 0) {
      const seatingData = [
        [t("seating.table_name"), t("report.guest_name"), t("guests.adults_short"), t("guests.children_short"), t("seating.persons_assigned")],
      ];
      seating.forEach(tb => {
        const tbGuests = (tb.guestIds || []).map(gid => guests.find(g => g.id === gid)).filter(Boolean);
        tbGuests.forEach(g => {
          seatingData.push([tb.name, g.name, g.adults || 1, g.children || 0, (g.adults || 1) + (g.children || 0)]);
        });
        if (tbGuests.length === 0) seatingData.push([tb.name, '-', '', '', '']);
      });
      seatingData.push(['']);
      const unassigned = guests.filter(g => !assignedGuestIds.has(g.id));
      if (unassigned.length > 0) {
        seatingData.push([t("seating.unassigned_guests")]);
        unassigned.forEach(g => seatingData.push(['', g.name, g.adults || 1, g.children || 0, (g.adults || 1) + (g.children || 0)]));
      }
      const seatingSheet = XLSX.utils.aoa_to_sheet(seatingData);
      XLSX.utils.book_append_sheet(workbook, seatingSheet, t("nav.seating"));
    }

    const fileName = `${currentEvent.name.replace(/[/\\:*?"<>|]/g, "_")}_${t("report.filename_suffix")}_${new Date().toISOString().slice(0, 19)}.xlsx`;
    XLSX.writeFile(workbook, fileName, {
      bookType: "xlsx",
      type: "binary",
      Props: {
        Title: `${t("common.appName")} - ${t("report.title")} ${currentEvent.name}`,
        Subject: t("report.workbook_subject"),
        Author: t("common.appName"),
        CreatedDate: new Date(),
      },
    });
  };

  return (
    <div>
      <div className="card shadow-sm">
        <div className="card-header bg-white fw-bold">
          👁️ {t("report.preview")}
        </div>
        <div className="card-body">
          {/* General Stats */}
          <div className="mb-4">
            <h5 className="text-primary">📊 {t("report.general_stats")}</h5>
            <div className="row g-2">
              <div className="col-6 col-md-3">
                <div className="border rounded p-2 text-center bg-light">
                  <div className="small text-muted">
                    {t("report.guest_groups")}
                  </div>
                  <div className="fs-4 fw-bold">{totalGuests}</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="border rounded p-2 text-center bg-light">
                  <div className="small text-muted">
                    {t("report.total_persons")}
                  </div>
                  <div className="fs-4 fw-bold">{totalPersons}</div>
                  <div className="small">
                    ({totalAdults} {t("report.adults")}, {totalChildren}{" "}
                    {t("report.children")})
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="border rounded p-2 text-center bg-light">
                  <div className="small text-muted">{t("guests.invited")}</div>
                  <div className="fs-4 fw-bold text-primary">
                    {invitedCount}
                  </div>
                  <div className="small">
                    {totalGuests > 0
                      ? Math.round((invitedCount / totalGuests) * 100)
                      : 0}
                    %
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="border rounded p-2 text-center bg-light">
                  <div className="small text-muted">
                    {t("tasks.tasks_preparations")}
                  </div>
                  <div className="fs-4 fw-bold">
                    {completedTasks}/{totalTasks}
                  </div>
                  <div className="small">{t("tasks.completed")}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Confirmation Breakdown */}
          <div className="mb-4">
            <h5 className="text-secondary">
              ✉️ {t("report.confirmation_breakdown")}
            </h5>
            <div className="row g-2">
              <div className="col-6 col-md-3">
                <div className="border rounded p-2 text-center bg-success bg-opacity-10">
                  <div className="small text-success fw-bold">✅ {t("guests.accepted")}</div>
                  <div className="fs-4 fw-bold text-success">{acceptedCount}</div>
                  <div className="small fw-semibold">{acceptedPersons.total} {t("guests.total_persons").toLowerCase()}</div>
                  <div className="small text-muted">{acceptedPersons.adults} {t("guests.adults_short")} · {acceptedPersons.children} {t("guests.children_short")}</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="border rounded p-2 text-center bg-warning bg-opacity-10">
                  <div className="small text-warning fw-bold">❓ {t("guests.maybe")}</div>
                  <div className="fs-4 fw-bold text-warning">{maybeCount}</div>
                  <div className="small fw-semibold">{maybePersons.total} {t("guests.total_persons").toLowerCase()}</div>
                  <div className="small text-muted">{maybePersons.adults} {t("guests.adults_short")} · {maybePersons.children} {t("guests.children_short")}</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="border rounded p-2 text-center bg-danger bg-opacity-10">
                  <div className="small text-danger fw-bold">❌ {t("guests.declined")}</div>
                  <div className="fs-4 fw-bold text-danger">{declinedCount}</div>
                  <div className="small fw-semibold">{declinedPersons.total} {t("guests.total_persons").toLowerCase()}</div>
                  <div className="small text-muted">{declinedPersons.adults} {t("guests.adults_short")} · {declinedPersons.children} {t("guests.children_short")}</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="border rounded p-2 text-center bg-secondary bg-opacity-10">
                  <div className="small text-muted">⏳ {t("guests.awaiting_confirmation")}</div>
                  <div className="fs-4 fw-bold text-secondary">{noResponseCount}</div>
                  <div className="small fw-semibold">{noResponsePersons.total} {t("guests.total_persons").toLowerCase()}</div>
                  <div className="small text-muted">{noResponsePersons.adults} {t("guests.adults_short")} · {noResponsePersons.children} {t("guests.children_short")}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Stats */}
          <div className="mb-4">
            <h5 className="text-success">💰 {t("report.financial_stats")}</h5>
            <div className="row g-2">
              <div className="col-12 col-md-4">
                <div className="border rounded p-2 text-center bg-success bg-opacity-10">
                  <div className="small text-success">
                    {t("report.total_income")}
                  </div>
                  <div className="fs-3 fw-bold text-success">
                    €{totalGifts.toFixed(2)}
                  </div>
                  <div className="small">
                    {t("report.from_gifts", { count: giftCount })}
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="border rounded p-2 text-center bg-danger bg-opacity-10">
                  <div className="small text-danger">
                    {t("report.total_expenses")}
                  </div>
                  <div className="fs-3 fw-bold text-danger">
                    €{totalExpenses.toFixed(2)}
                  </div>
                  <div className="small">
                    {services.length} {t("report.services_count")}
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div
                  className={`border rounded p-2 text-center ${balance >= 0 ? "bg-success bg-opacity-25" : "bg-danger bg-opacity-25"}`}
                >
                  <div className="small">{t("report.net_balance")}</div>
                  <div
                    className={`fs-3 fw-bold ${balance >= 0 ? "text-success" : "text-danger"}`}
                  >
                    {balance >= 0 ? "+" : ""}€{balance.toFixed(2)}
                  </div>
                  <div className="small">
                    {balance >= 0 ? t("report.profitable") : t("report.loss")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Guest List preview */}
          <div className="mb-4">
            <h5 className="text-info">👥 {t("report.guest_list")}</h5>
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>{t("report.guest_name")}</th>
                    <th>{t("guests.invited_short")}</th>
                    <th>{t("guests.confirmed_short")}</th>
                    <th>{t("guests.adults_short")}</th>
                    <th>{t("guests.children_short")}</th>
                    <th>{t("report.guest_amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {guests.slice(0, 10).map((g) => {
                    const conf = normalizeConfirmed(g.confirmed);
                    const rowClass =
                      conf === "accepted"
                        ? "table-success"
                        : conf === "declined"
                          ? "table-danger"
                          : conf === "maybe"
                            ? "table-warning"
                            : "";
                    return (
                      <tr key={g.id} className={rowClass}>
                        <td>{g.name}</td>
                        <td className="text-center">
                          {g.invited ? "✅" : "❌"}
                        </td>
                        <td className="text-center">
                          {confirmationLabel(g.confirmed)}
                        </td>
                        <td className="text-center">{g.adults || 1}</td>
                        <td className="text-center">{g.children || 0}</td>
                        <td className="text-end">
                          {g.giftValue ? `€${g.giftValue.toFixed(2)}` : "-"}
                        </td>
                      </tr>
                    );
                  })}
                  {guests.length > 10 && (
                    <tr>
                      <td colSpan="6" className="text-muted text-center">
                        {t("report.more_guests", { count: guests.length - 10 })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Services preview */}
          <div className="mb-4">
            <h5 className="text-danger">🛠️ {t("report.services")}</h5>
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>{t("report.service_name")}</th>
                    <th>{t("report.service_cost")}</th>
                    <th>{t("report.service_status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td className="text-end">€{s.cost.toFixed(2)}</td>
                      <td>
                        {s.paid
                          ? `✅ ${t("report.paid")}`
                          : `⏳ ${t("report.pending")}`}
                      </td>
                    </tr>
                  ))}
                  {services.length === 0 && (
                    <tr>
                      <td colSpan="3" className="text-muted text-center">
                        {t("report.no_services")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Seating preview */}
          {seating.length > 0 && (
            <div className="mb-4">
              <h5 className="text-primary">🪑 {t("nav.seating")}</h5>
              <div className="row g-2 mb-3">
                <div className="col-6 col-md-3">
                  <div className="border rounded p-2 text-center bg-light">
                    <div className="small text-muted">{t("seating.total_tables")}</div>
                    <div className="fs-4 fw-bold text-primary">{seating.length}</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="border rounded p-2 text-center bg-light">
                    <div className="small text-muted">{t("seating.total_seats")}</div>
                    <div className="fs-4 fw-bold text-success">{totalSeats}</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="border rounded p-2 text-center bg-light">
                    <div className="small text-muted">{t("seating.persons_seated")}</div>
                    <div className="fs-4 fw-bold text-info">{totalSeated}</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="border rounded p-2 text-center bg-light">
                    <div className="small text-muted">{t("seating.unassigned_guests")}</div>
                    <div className={`fs-4 fw-bold ${unassignedCount > 0 ? 'text-warning' : 'text-secondary'}`}>{unassignedCount}</div>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>{t("seating.table_name")}</th>
                      <th className="text-center">{t("seating.seats")}</th>
                      <th className="text-center">{t("seating.baby_seats")}</th>
                      <th className="text-center">{t("seating.stroller_spaces")}</th>
                      <th className="text-center">{t("seating.persons_assigned")}</th>
                      <th className="text-center">{t("seating.empty_seats")}</th>
                      <th>{t("seating.assigned_guests")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seating.map(tb => {
                      const persons = tablePersons(tb);
                      const empty = Math.max(0, tb.seats - persons);
                      const tbGuests = (tb.guestIds || []).map(gid => guests.find(g => g.id === gid)).filter(Boolean);
                      return (
                        <tr key={tb.id}>
                          <td className="fw-semibold">{tb.name}</td>
                          <td className="text-center">{tb.seats}</td>
                          <td className="text-center">{tb.babySeats || 0}</td>
                          <td className="text-center">{tb.strollerSpaces || 0}</td>
                          <td className="text-center text-info fw-bold">{persons}</td>
                          <td className={`text-center fw-bold ${empty > 0 ? 'text-warning' : 'text-secondary'}`}>{empty}</td>
                          <td><small>{tbGuests.map(g => g.name).join(', ') || '-'}</small></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="text-center mt-3">
            <button className="btn btn-success btn-lg" onClick={exportToExcel}>
              {t("report.export")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
